/**
 * Token storage for Loyalty Lane CST (Client Session Token) and SST (Shopper Session Token).
 *
 * CST -- app-level token obtained via POST /api/ClientSession.
 *   Regular expiry: 20 hours. Admin expiry: 45 minutes.
 *
 * SST -- per-user token obtained via POST /api/ShopperSession.
 *   Expiry: 45 minutes (refreshed on each use by the API).
 *
 * Tokens are cached in memory for speed and persisted to localStorage
 * with lightweight obfuscation to discourage casual inspection.
 */

import type {
  LLApiResponse,
  ClientSessionData,
  ShopperSessionData,
} from '@/Coupons/types/loyalty-lane';

// ---------------------------------------------------------------------------
// Obfuscation helpers
// ---------------------------------------------------------------------------

const obfuscate = (value: string): string => {
  try {
    return btoa(value).split('').reverse().join('');
  } catch {
    return value;
  }
};

const deobfuscate = (value: string): string => {
  try {
    return atob(value.split('').reverse().join(''));
  } catch {
    return value;
  }
};

// ---------------------------------------------------------------------------
// Storage keys
// ---------------------------------------------------------------------------

const CST_KEY = 'll_cst';
const CST_EXPIRY_KEY = 'll_cst_exp';
const SST_KEY = 'll_sst';
const SST_EXPIRY_KEY = 'll_sst_exp';
const SHOPPER_ID_KEY = 'll_shopper_id';
const ACCOUNT_ID_KEY = 'll_account_id';
const IS_FULL_LOGIN_KEY = 'll_full_login';
const CLIENT_CONFIG_KEY = 'll_client_config';

// Default expiry durations (milliseconds)
const CST_EXPIRY_MS = 20 * 60 * 60 * 1000; // 20 hours
const SST_EXPIRY_MS = 45 * 60 * 1000; // 45 minutes

// ---------------------------------------------------------------------------
// In-memory cache
// ---------------------------------------------------------------------------

let cachedCST: string | null = null;
let cachedCSTExpiry: number | null = null;
let cachedSST: string | null = null;
let cachedSSTExpiry: number | null = null;
let cachedShopperId: number | null = null;
let cachedAccountId: number | null = null;
let cachedIsFullLogin: boolean | null = null;

// In-flight CST fetch promise to prevent duplicate requests
let cstFetchPromise: Promise<string> | null = null;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function readToken(key: string, expiryKey: string): { token: string; expiry: number } | null {
  const raw = localStorage.getItem(key);
  const exp = localStorage.getItem(expiryKey);
  if (!raw || !exp) return null;

  const expiry = Number(exp);
  if (Date.now() >= expiry) {
    localStorage.removeItem(key);
    localStorage.removeItem(expiryKey);
    return null;
  }

  return { token: deobfuscate(raw), expiry };
}

function writeToken(key: string, expiryKey: string, token: string, expiry: number): void {
  localStorage.setItem(key, obfuscate(token));
  localStorage.setItem(expiryKey, String(expiry));
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const TokenStorage = {
  // ---- CST (Client Session Token) ----

  /**
   * Returns a valid CST, fetching a new one if missing or expired.
   * Concurrent calls share the same in-flight request.
   */
  async getClientToken(): Promise<string> {
    // Check memory cache first
    if (cachedCST && cachedCSTExpiry && Date.now() < cachedCSTExpiry) {
      return cachedCST;
    }

    // Check localStorage
    const stored = readToken(CST_KEY, CST_EXPIRY_KEY);
    if (stored) {
      cachedCST = stored.token;
      cachedCSTExpiry = stored.expiry;
      return stored.token;
    }

    // Fetch new CST -- deduplicate concurrent calls
    if (!cstFetchPromise) {
      cstFetchPromise = this.fetchClientToken().finally(() => {
        cstFetchPromise = null;
      });
    }
    return cstFetchPromise;
  },

  /**
   * Fetch a fresh CST from the API.
   * Called automatically by getClientToken() when needed.
   */
  async fetchClientToken(): Promise<string> {
    const baseUrl = import.meta.env.VITE_LOYALTY_LANE_API;
    const username = import.meta.env.VITE_LOYALTY_LANE_CLIENT_USERNAME;
    const password = import.meta.env.VITE_LOYALTY_LANE_CLIENT_PASSWORD;

    if (!baseUrl || !username || !password) {
      throw new Error(
        'Missing Loyalty Lane env vars: VITE_LOYALTY_LANE_API, VITE_LOYALTY_LANE_CLIENT_USERNAME, VITE_LOYALTY_LANE_CLIENT_PASSWORD',
      );
    }

    const url = `${baseUrl.replace(/\/$/, '')}/ClientSession`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      throw new Error(`CST fetch failed: ${response.status} ${response.statusText}`);
    }

    const json: LLApiResponse<ClientSessionData> = await response.json();
    if (json.result !== 'OK' || !json.data?.ClientToken) {
      throw new Error(`CST fetch error: ${json.message}`);
    }

    const token = json.data.ClientToken;
    const expiry = Date.now() + CST_EXPIRY_MS;

    cachedCST = token;
    cachedCSTExpiry = expiry;
    writeToken(CST_KEY, CST_EXPIRY_KEY, token, expiry);

    // Persist ClientConfiguration for feature flags (e.g. HideUntargettedOffers)
    if (json.data.ClientConfiguration) {
      try {
        localStorage.setItem(CLIENT_CONFIG_KEY, JSON.stringify(json.data.ClientConfiguration));
      } catch {
        // Silently fail
      }
    }

    return token;
  },

  /** Force-clear the CST (triggers a fresh fetch on next getClientToken call). */
  clearClientToken(): void {
    cachedCST = null;
    cachedCSTExpiry = null;
    localStorage.removeItem(CST_KEY);
    localStorage.removeItem(CST_EXPIRY_KEY);
  },

  // ---- SST (Shopper Session Token) ----

  /** Store the SST and associated shopper metadata after successful authentication. */
  setShopperSession(data: ShopperSessionData): void {
    const expiry = Date.now() + SST_EXPIRY_MS;

    cachedSST = data.token;
    cachedSSTExpiry = expiry;
    cachedShopperId = data.shopperid;
    cachedAccountId = data.accountid;
    cachedIsFullLogin = data.isfulllogin;

    writeToken(SST_KEY, SST_EXPIRY_KEY, data.token, expiry);
    localStorage.setItem(SHOPPER_ID_KEY, String(data.shopperid));
    localStorage.setItem(ACCOUNT_ID_KEY, String(data.accountid));
    localStorage.setItem(IS_FULL_LOGIN_KEY, String(data.isfulllogin));
  },

  /** Returns the cached SST, or null if missing/expired. */
  getShopperToken(): string | null {
    if (cachedSST && cachedSSTExpiry && Date.now() < cachedSSTExpiry) {
      return cachedSST;
    }

    const stored = readToken(SST_KEY, SST_EXPIRY_KEY);
    if (stored) {
      cachedSST = stored.token;
      cachedSSTExpiry = stored.expiry;
      return stored.token;
    }

    return null;
  },

  /** Clear SST and all shopper metadata (sign-out). */
  clearShopperToken(): void {
    cachedSST = null;
    cachedSSTExpiry = null;
    cachedShopperId = null;
    cachedAccountId = null;
    cachedIsFullLogin = null;

    localStorage.removeItem(SST_KEY);
    localStorage.removeItem(SST_EXPIRY_KEY);
    localStorage.removeItem(SHOPPER_ID_KEY);
    localStorage.removeItem(ACCOUNT_ID_KEY);
    localStorage.removeItem(IS_FULL_LOGIN_KEY);
  },

  // ---- Shopper metadata helpers ----

  getShopperId(): number | null {
    if (cachedShopperId !== null) return cachedShopperId;
    const raw = localStorage.getItem(SHOPPER_ID_KEY);
    if (raw) {
      cachedShopperId = Number(raw);
      return cachedShopperId;
    }
    return null;
  },

  getAccountId(): number | null {
    if (cachedAccountId !== null) return cachedAccountId;
    const raw = localStorage.getItem(ACCOUNT_ID_KEY);
    if (raw) {
      cachedAccountId = Number(raw);
      return cachedAccountId;
    }
    return null;
  },

  isFullLogin(): boolean {
    if (cachedIsFullLogin !== null) return cachedIsFullLogin;
    const raw = localStorage.getItem(IS_FULL_LOGIN_KEY);
    return raw === 'true';
  },

  /** Extend the local SST expiry (call after a successful authenticated request). */
  touchShopperExpiry(): void {
    if (cachedSST) {
      const expiry = Date.now() + SST_EXPIRY_MS;
      cachedSSTExpiry = expiry;
      localStorage.setItem(SST_EXPIRY_KEY, String(expiry));
    }
  },

  isAuthenticated(): boolean {
    return this.getShopperToken() !== null;
  },

  // ---- Client Configuration ----

  /** Returns the HideUntargettedOffers flag from the ClientSession config. */
  getHideUntargetedOffers(): boolean {
    try {
      const raw = localStorage.getItem(CLIENT_CONFIG_KEY);
      if (!raw) return false;
      const config = JSON.parse(raw);
      return config.HideUntargettedOffers === true;
    } catch {
      return false;
    }
  },

  // ---- Lifecycle ----

  /** Clear all tokens and metadata. */
  clearAll(): void {
    this.clearClientToken();
    this.clearShopperToken();
    localStorage.removeItem(CLIENT_CONFIG_KEY);
  },
};
